import { Component, Input, Output, EventEmitter } from '@angular/core';
import { Buyer, BuyerOrigin, BuyersService } from 'src/app/core/buyers.service';
import { DialogService } from 'src/app/core/dialog.service';
import { UtilsService } from 'src/app/core/utils.service';
import { ErrorService, SplittypayError } from 'src/app/core/error.service';
import { BUYERS_TABLE_COLUMNS } from 'src/app/shared/constants/operationals';
import { BuyersOnboardingDialogComponent } from '../buyers-onboarding-dialog/buyers-onboarding-dialog.component';
import { BuyersEditDialogComponent } from '../buyers-edit-dialog/buyers-edit-dialog.component';
import { BuyersRequestsDialogComponent } from '../buyers-requests-dialog/buyers-requests-dialog.component';
import { BuyersEditCardDialogComponent } from '../buyers-edit-card-dialog/buyers-edit-card-dialog.component';
import { BuyersEditIbanDialogComponent } from '../buyers-edit-iban-dialog/buyers-edit-iban-dialog.component';
import { BuyersAccessControlDialogComponent } from '../buyers-access-control-dialog/buyers-access-control-dialog.component';
import { BuyersSetBlockByAdminDialogComponent } from '../buyers-set-block-by-admin-dialog/buyers-set-block-by-admin-dialog.component';
import { BuyersConfirmOtpDialogComponent } from '../buyers-confirm-otp-dialog/buyers-confirm-otp-dialog.component';
import { NavigationService } from 'src/app/core/navigation.service';

@Component({
  selector: 'app-buyers-data-table',
  templateUrl: './buyers-data-table.component.html',
  styleUrls: ['./buyers-data-table.component.scss'],
})
export class BuyersDataTableComponent {
  @Input() buyers: Buyer[] = [];
  @Output() refresh: EventEmitter<void> = new EventEmitter<void>();
  @Output() scrolledToEnd: EventEmitter<void> = new EventEmitter<void>();
  displayed_columns: string[] = BUYERS_TABLE_COLUMNS;
  in_store_origin: BuyerOrigin = BuyerOrigin.in_store;
  website_origin: BuyerOrigin = BuyerOrigin.website;
  in_order_origin: BuyerOrigin = BuyerOrigin.in_order;

  constructor(
    private readonly utils_service: UtilsService,
    private readonly dialog_service: DialogService,
    private readonly error_service: ErrorService,
    private readonly navigation_service: NavigationService,
    private readonly buyer_service: BuyersService,
  ) {}
  onInfoClick(buyer: Buyer): void {
    this.dialog_service.openDialog(BuyersOnboardingDialogComponent, { buyer });
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const end_offset = 200;
    if (element) {
      if (element.offsetHeight + element.scrollTop >= element.scrollHeight - end_offset) {
        this.scrolledToEnd.emit();
      }
    }
  }

  onChatClick(buyer: Buyer): void {
    this.buyer_service.getWhatsappLink(buyer.buyer_id!).subscribe({
      next: response => {
        window.open(response.data.whatsapp_url, '_blank');
      },
      error: err => {
        const processed_error: SplittypayError = err;
        this.error_service.processApiError(processed_error.code, processed_error.message);
      },
    });
  }

  onEditClick(buyer: Buyer): void {
    this.dialog_service
      .openDialog(BuyersEditDialogComponent, { buyer })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result?.success) {
          this.refresh.emit();
        }

        if (dialog_result?.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onEditCreditCardClick(buyer: Buyer): void {
    this.dialog_service
      .openDialog(BuyersEditCardDialogComponent, { buyer })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result?.success) {
          this.refresh.emit();
        }

        if (dialog_result?.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onEditIbanClick(buyer: Buyer): void {
    this.dialog_service
      .openDialog(BuyersEditIbanDialogComponent, {
        buyer_id: buyer.buyer_id,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result?.success) {
          this.refresh.emit();
        }

        if (dialog_result?.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onToggleLockBuyer(buyer: Buyer): void {
    this.dialog_service
      .openDialog(BuyersSetBlockByAdminDialogComponent, { buyer })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result?.success) {
          this.refresh.emit();
        }
        if (dialog_result?.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onUnlockBuyerClick(buyer: Buyer) {
    this.dialog_service
      .openDialog(BuyersAccessControlDialogComponent, {
        buyer,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result?.success) {
          this.refresh.emit();
        }

        if (dialog_result?.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onConfirmBuyerClick(buyer: Buyer): void {
    this.dialog_service
      .openDialog(BuyersConfirmOtpDialogComponent, {
        buyer,
      })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result?.success) {
          this.refresh.emit();
        }

        if (dialog_result?.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onRequestsClick(buyer: Buyer): void {
    this.dialog_service.openDialog(BuyersRequestsDialogComponent, {
      buyer_id: buyer.buyer_id,
    });
  }

  onDetailsClick(buyer: Buyer): void {
    this.navigation_service.toBuyerDetails(buyer.buyer_id as string);
  }

  getOnboardingStatus(buyer: Buyer): string {
    return this.utils_service.getOnboardingStatus(buyer);
  }

  getFullName(buyer: Buyer): string {
    return `${buyer.firstname} ${buyer.lastname}`;
  }

  getOriginLabel(origin?: BuyerOrigin): string {
    return this.utils_service.getBuyerOriginLabel(origin);
  }
}
